import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Assignment as AssignmentIcon,
  BarChart as StatsIcon,
  ContentCopy as DuplicateIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  PowerSettingsNew as PowerIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import assessmentService, { Assessment, AssessmentStatistics } from '../services/assessmentService';
import { toast } from 'react-toastify';

const Assessments: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(undefined);
  const [categories, setCategories] = useState<string[]>([]);
  const [statsDialog, setStatsDialog] = useState(false);
  const [selectedStats, setSelectedStats] = useState<AssessmentStatistics | null>(null);

  useEffect(() => {
    loadAssessments();
    loadCategories();
  }, [page, rowsPerPage, search, categoryFilter, activeFilter]);

  const loadAssessments = async () => {
    try {
      setLoading(true);
      const response = await assessmentService.getAssessments(
        page,
        rowsPerPage,
        search || undefined,
        categoryFilter || undefined,
        activeFilter
      );
      setAssessments(response.content);
      setTotalElements(response.totalElements);
    } catch (error) {
      console.error('Error loading assessments:', error);
      toast.error('Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const cats = await assessmentService.getAssessmentCategories();
      setCategories(cats);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleViewAssessment = (id: number) => {
    navigate(`/assessments/${id}`);
  };

  const handleEditAssessment = (id: number) => {
    navigate(`/assessments/${id}/edit`);
  };

  const handleAssignAssessment = (id: number) => {
    navigate(`/assessments/${id}/assign`);
  };

  const handleDuplicateAssessment = async (id: number) => {
    try {
      await assessmentService.duplicateAssessment(id);
      toast.success('Assessment duplicated successfully');
      loadAssessments();
    } catch (error) {
      console.error('Error duplicating assessment:', error);
      toast.error('Failed to duplicate assessment');
    }
  };

  const handleToggleActive = async (assessment: Assessment) => {
    try {
      if (assessment.isActive) {
        await assessmentService.deactivateAssessment(assessment.id);
        toast.success('Assessment deactivated');
      } else {
        await assessmentService.activateAssessment(assessment.id);
        toast.success('Assessment activated');
      }
      loadAssessments();
    } catch (error) {
      console.error('Error toggling assessment status:', error);
      toast.error('Failed to update assessment status');
    }
  };

  const handleDeleteAssessment = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this assessment?')) {
      try {
        await assessmentService.deleteAssessment(id);
        toast.success('Assessment deleted successfully');
        loadAssessments();
      } catch (error) {
        console.error('Error deleting assessment:', error);
        toast.error('Failed to delete assessment');
      }
    }
  };

  const handleViewStatistics = async (assessment: Assessment) => {
    try {
      const stats = await assessmentService.getAssessmentStatistics(assessment.id);
      setSelectedStats(stats);
      setStatsDialog(true);
    } catch (error) {
      console.error('Error loading statistics:', error);
      toast.error('Failed to load statistics');
    }
  };

  const isAdmin = user?.userType === 'ROLE_ADMIN';
  const isProvider = user?.userType === 'ROLE_PROVIDER';
  const canManageAssessments = isAdmin || isProvider;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Assessments
        </Typography>
        {canManageAssessments && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/assessments/new')}
          >
            Create Assessment
          </Button>
        )}
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search assessments..."
                value={search}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  label="Category"
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={activeFilter === undefined ? '' : activeFilter.toString()}
                  onChange={(e) => {
                    const value = e.target.value;
                    setActiveFilter(value === '' ? undefined : value === 'true');
                  }}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Category</TableCell>
                <TableCell align="center">Questions</TableCell>
                <TableCell align="center">Est. Time</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Risk Thresholds</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assessments.map((assessment) => (
                <TableRow key={assessment.id}>
                  <TableCell>
                    <Typography variant="subtitle2">{assessment.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {assessment.description}
                    </Typography>
                  </TableCell>
                  <TableCell>{assessment.category}</TableCell>
                  <TableCell align="center">{assessment.totalQuestions}</TableCell>
                  <TableCell align="center">{assessment.estimatedMinutes} min</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={assessment.isActive ? 'Active' : 'Inactive'}
                      color={assessment.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap={0.5} justifyContent="center">
                      <Chip label={`R: ${assessment.redFlagThreshold}`} color="error" size="small" />
                      <Chip label={`Y: ${assessment.yellowFlagThreshold}`} color="warning" size="small" />
                      <Chip label={`G: ${assessment.greenFlagThreshold}`} color="success" size="small" />
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" gap={0.5} justifyContent="flex-end">
                      <Tooltip title="View">
                        <IconButton size="small" onClick={() => handleViewAssessment(assessment.id)}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canManageAssessments && (
                        <>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleEditAssessment(assessment.id)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Assign">
                            <IconButton size="small" onClick={() => handleAssignAssessment(assessment.id)}>
                              <AssignmentIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Statistics">
                            <IconButton size="small" onClick={() => handleViewStatistics(assessment)}>
                              <StatsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Duplicate">
                            <IconButton size="small" onClick={() => handleDuplicateAssessment(assessment.id)}>
                              <DuplicateIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={assessment.isActive ? 'Deactivate' : 'Activate'}>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleActive(assessment)}
                              color={assessment.isActive ? 'error' : 'success'}
                            >
                              <PowerIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {isAdmin && (
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteAssessment(assessment.id)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalElements}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      {/* Statistics Dialog */}
      <Dialog open={statsDialog} onClose={() => setStatsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assessment Statistics</DialogTitle>
        <DialogContent>
          {selectedStats && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="h6">{selectedStats.assessmentTitle}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4">{selectedStats.totalAssignments}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Assignments
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4">{selectedStats.completionRate.toFixed(1)}%</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completion Rate
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light' }}>
                  <Typography variant="h5">{selectedStats.redFlagCount}</Typography>
                  <Typography variant="body2">High Risk</Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light' }}>
                  <Typography variant="h5">{selectedStats.yellowFlagCount}</Typography>
                  <Typography variant="body2">Medium Risk</Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
                  <Typography variant="h5">{selectedStats.greenFlagCount}</Typography>
                  <Typography variant="body2">Low Risk</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Average Score
                  </Typography>
                  <Typography variant="h5">{selectedStats.averageScore.toFixed(1)}</Typography>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Assessments;